import { supabase } from '../lib/supabase';

export const notificationEngineService = {
  // ============================================================================
  // NOTIFICATION ROUTING
  // ============================================================================

  async routeNotification(notification) {
    const { data, error } = await supabase
      ?.rpc('route_notification', {
        p_notification_type: notification?.notificationType,
        p_priority: notification?.priority,
        p_entity_type: notification?.entityType,
        p_entity_id: notification?.entityId,
        p_subject: notification?.subject,
        p_message: notification?.message,
        p_metadata: notification?.metadata
      });
    
    if (error) throw error;
    return data;
  },

  async createNotification(notification) {
    const { data, error } = await supabase
      ?.from('notification_queue')
      ?.insert({
        notification_type: notification?.notificationType,
        priority: notification?.priority,
        recipient_email: notification?.recipientEmail,
        recipient_role: notification?.recipientRole,
        subject: notification?.subject,
        message: notification?.message,
        entity_type: notification?.entityType,
        entity_id: notification?.entityId,
        metadata: notification?.metadata,
        scheduled_for: notification?.scheduledFor || new Date()?.toISOString()
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      notificationType: data?.notification_type,
      priority: data?.priority,
      recipientEmail: data?.recipient_email,
      subject: data?.subject,
      notificationStatus: data?.notification_status,
      scheduledFor: data?.scheduled_for,
      createdAt: data?.created_at
    };
  },

  // ============================================================================
  // NOTIFICATION QUEUE MANAGEMENT
  // ============================================================================

  async getPendingNotifications() {
    const { data, error } = await supabase
      ?.from('notification_queue')
      ?.select('*')
      ?.eq('notification_status', 'pending')
      ?.lte('scheduled_for', new Date()?.toISOString())
      ?.order('priority', { ascending: false })
      ?.order('scheduled_for', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      notificationType: row?.notification_type,
      priority: row?.priority,
      recipientEmail: row?.recipient_email,
      recipientRole: row?.recipient_role,
      subject: row?.subject,
      message: row?.message,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      metadata: row?.metadata,
      notificationStatus: row?.notification_status,
      scheduledFor: row?.scheduled_for,
      createdAt: row?.created_at
    })) || [];
  },

  async getNotificationsByRecipient(recipientEmail) {
    const { data, error } = await supabase
      ?.from('notification_queue')
      ?.select('*')
      ?.eq('recipient_email', recipientEmail)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      notificationType: row?.notification_type,
      priority: row?.priority,
      subject: row?.subject,
      message: row?.message,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      notificationStatus: row?.notification_status,
      scheduledFor: row?.scheduled_for,
      sentAt: row?.sent_at,
      readAt: row?.read_at,
      createdAt: row?.created_at
    })) || [];
  },

  async updateNotificationStatus(notificationId, status, updates = {}) {
    const updateData = {
      notification_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'sent') {
      updateData.sent_at = new Date()?.toISOString();
    } else if (status === 'delivered') {
      updateData.delivered_at = new Date()?.toISOString();
    } else if (status === 'read') {
      updateData.read_at = new Date()?.toISOString();
    } else if (status === 'failed') {
      updateData.error_message = updates?.errorMessage;
      updateData.retry_count = (updates?.retryCount || 0) + 1;
    }

    const { data, error } = await supabase
      ?.from('notification_queue')
      ?.update(updateData)
      ?.eq('id', notificationId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      notificationStatus: data?.notification_status,
      sentAt: data?.sent_at,
      deliveredAt: data?.delivered_at,
      readAt: data?.read_at
    };
  },

  async markAsRead(notificationId) {
    return this.updateNotificationStatus(notificationId, 'read');
  },

  async markAllAsRead(recipientEmail) {
    const { error } = await supabase
      ?.from('notification_queue')
      ?.update({
        notification_status: 'read',
        read_at: new Date()?.toISOString()
      })
      ?.eq('recipient_email', recipientEmail)
      ?.in('notification_status', ['pending', 'sent', 'delivered']);
    
    if (error) throw error;
  },

  // ============================================================================
  // NOTIFICATION ROUTING RULES
  // ============================================================================

  async getAllRoutingRules() {
    const { data, error } = await supabase
      ?.from('notification_routing_rules')
      ?.select('*')
      ?.order('priority', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      ruleName: row?.rule_name,
      notificationType: row?.notification_type,
      priority: row?.priority,
      entityType: row?.entity_type,
      conditionCriteria: row?.condition_criteria,
      routeToRole: row?.route_to_role,
      routeToEmail: row?.route_to_email,
      routeToDepartment: row?.route_to_department,
      escalationDelayMinutes: row?.escalation_delay_minutes,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async createRoutingRule(rule) {
    const { data, error } = await supabase
      ?.from('notification_routing_rules')
      ?.insert({
        rule_name: rule?.ruleName,
        notification_type: rule?.notificationType,
        priority: rule?.priority,
        entity_type: rule?.entityType,
        condition_criteria: rule?.conditionCriteria,
        route_to_role: rule?.routeToRole,
        route_to_email: rule?.routeToEmail,
        route_to_department: rule?.routeToDepartment,
        escalation_delay_minutes: rule?.escalationDelayMinutes || 0,
        is_active: rule?.isActive !== undefined ? rule?.isActive : true
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      ruleName: data?.rule_name,
      notificationType: data?.notification_type,
      priority: data?.priority,
      isActive: data?.is_active
    };
  },

  async updateRoutingRule(ruleId, updates) {
    const updateData = {};
    
    if (updates?.ruleName) updateData.rule_name = updates?.ruleName;
    if (updates?.routeToRole) updateData.route_to_role = updates?.routeToRole;
    if (updates?.routeToEmail) updateData.route_to_email = updates?.routeToEmail;
    if (updates?.routeToDepartment) updateData.route_to_department = updates?.routeToDepartment;
    if (updates?.escalationDelayMinutes !== undefined) updateData.escalation_delay_minutes = updates?.escalationDelayMinutes;
    if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;
    
    updateData.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('notification_routing_rules')
      ?.update(updateData)
      ?.eq('id', ruleId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      ruleName: data?.rule_name,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  // ============================================================================
  // NOTIFICATION PREFERENCES
  // ============================================================================

  async getUserPreferences(userEmail) {
    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.select('*')
      ?.eq('user_email', userEmail)
      ?.single();
    
    if (error && error?.code !== 'PGRST116') throw error;
    
    if (!data) {
      return {
        enableEmail: true,
        enableInApp: true,
        enablePush: false,
        priorityThreshold: 'low'
      };
    }
    
    return {
      id: data?.id,
      userEmail: data?.user_email,
      enableEmail: data?.enable_email,
      enableInApp: data?.enable_in_app,
      enablePush: data?.enable_push,
      priorityThreshold: data?.priority_threshold,
      quietHoursStart: data?.quiet_hours_start,
      quietHoursEnd: data?.quiet_hours_end,
      notificationTypes: data?.notification_types
    };
  },

  async updateUserPreferences(userEmail, preferences) {
    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.upsert({
        user_email: userEmail,
        enable_email: preferences?.enableEmail,
        enable_in_app: preferences?.enableInApp,
        enable_push: preferences?.enablePush,
        priority_threshold: preferences?.priorityThreshold,
        quiet_hours_start: preferences?.quietHoursStart,
        quiet_hours_end: preferences?.quietHoursEnd,
        notification_types: preferences?.notificationTypes,
        updated_at: new Date()?.toISOString()
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      userEmail: data?.user_email,
      enableEmail: data?.enable_email,
      enableInApp: data?.enable_in_app,
      enablePush: data?.enable_push,
      priorityThreshold: data?.priority_threshold
    };
  },

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToNotifications(recipientEmail, callback) {
    const channel = supabase
      ?.channel('notification_queue_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_queue',
          filter: `recipient_email=eq.${recipientEmail}`
        },
        (payload) => {
          const newNotification = {
            id: payload?.new?.id,
            notificationType: payload?.new?.notification_type,
            priority: payload?.new?.priority,
            subject: payload?.new?.subject,
            message: payload?.new?.message,
            entityType: payload?.new?.entity_type,
            entityId: payload?.new?.entity_id,
            notificationStatus: payload?.new?.notification_status,
            scheduledFor: payload?.new?.scheduled_for,
            createdAt: payload?.new?.created_at
          };
          callback(newNotification);
        }
      )
      ?.subscribe();
    
    return channel;
  },

  unsubscribeFromNotifications(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getNotificationStats(recipientEmail) {
    const { data, error } = await supabase
      ?.from('notification_queue')
      ?.select('notification_type, priority, notification_status')
      ?.eq('recipient_email', recipientEmail);
    
    if (error) throw error;
    
    const stats = {
      total: data?.length || 0,
      unread: data?.filter(n => n?.notification_status === 'pending' || n?.notification_status === 'sent')?.length || 0,
      critical: data?.filter(n => n?.priority === 'critical')?.length || 0,
      high: data?.filter(n => n?.priority === 'high')?.length || 0,
      byType: {
        approvalRequest: data?.filter(n => n?.notification_type === 'approval_request')?.length || 0,
        escalation: data?.filter(n => n?.notification_type === 'escalation_triggered')?.length || 0,
        payrollAlert: data?.filter(n => n?.notification_type === 'payroll_alert')?.length || 0,
        leaveAlert: data?.filter(n => n?.notification_type === 'leave_alert')?.length || 0
      }
    };
    
    return stats;
  }
};

export default notificationEngineService;